import { PagedResult, Result } from "../../../models/Result";
import { SelectItem } from "../../../models/SelectItem";
import { DocumentTypeModel } from "../../../models/DocumentTypeModel";
import { IndividualStatus, UpdateUserPhotoRequest, UpdateUserProfileRequest, User, UserBalance, UserProfileUpdate } from "../../../models/User";
import { httpService } from "../../HttpService";
import { ICrudApiService } from "../../ICrudApiService";
import { SearchRequestBaseModel } from "../../../models/SearchRequestModel";
import { ValidateIbanRequest, ValidateIbanResponse } from "../../../models/MoneyTransferModal";
import { IndividualList, IndividualSelectModel } from "../../../models/IndividualSelectModel";
import { RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS, ResidencePermitResponse, SaveResidencePermitRequest, SaveResidencePermitResult } from "../../../models/ResidencePermit";


class UsersApiService implements ICrudApiService<User, UserProfileUpdate> {
    path: string = "Individual";

    onlyIndividuals = async (request: SearchRequestBaseModel): Promise<PagedResult<User[]>> => await httpService.getPage<User[]>(`${this.path}/only-individuals`, request);

    onlyRegisteredUsers = async (request: SearchRequestBaseModel): Promise<PagedResult<User[]>> => await httpService.getPage<User[]>(`${this.path}/only-registered-users`, request);
   
    updateRegisteredUser = async (request: UpdateUserProfileRequest): Promise<Result<void>> => await httpService.put(`${this.path}/update-user-profile`, request);

    getAll = async (request: SearchRequestBaseModel): Promise<PagedResult<User[]>> => await httpService.getPage<User[]>(`${this.path}/search`, request);

    get = async (userId: number): Promise<Result<User>> => await httpService.get<User>(`/${this.path}/${userId}`);

    getRegisteredUser = async (userId: number): Promise<Result<User>> => await httpService.get<User>(`/${this.path}/user-profile/${userId}`);

    getFromSubmission = async (submissionId: number): Promise<Result<User>> => await httpService.get<User>(`/${this.path}/from-submission/${submissionId}`);

    getUserBalance = async (userId: number): Promise<Result<UserBalance>> => await httpService.get<UserBalance>(`/${this.path}/${userId}/balance`);

    create = async (user: UserProfileUpdate): Promise<Result<User>> => await httpService.post<User>(`/${this.path}`, user);

    update = async (user: UserProfileUpdate): Promise<Result<User>> => await httpService.put<User>(`/${this.path}`, user);

    updateForCompliance = async (user: UserProfileUpdate): Promise<Result<User>> => await httpService.put<User>(`/${this.path}/for-compliance`, user);

    // 200 with the EU/EEA flag, the edit flag and every recorded permit (active first). The three
    // residence permit calls use RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS instead of the 120 s default.
    getResidencePermit = async (individualId: number): Promise<Result<ResidencePermitResponse>> =>
        await httpService.get<ResidencePermitResponse>(`/${this.path}/${individualId}/residence-permit`, { timeout: RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS });

    // Records the permit on "Done". The route takes the KYC submission id; the body carries the individual id.
    saveResidencePermit = async (submissionId: number, request: SaveResidencePermitRequest): Promise<Result<SaveResidencePermitResult>> => await httpService.post<SaveResidencePermitResult>(`/${this.path}/${submissionId}/residence-permit`, request, { timeout: RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS });

    // EU/EEA nationals only: marks the residence-permit requirement as not applicable on the submission. No body.
    markResidencePermitNotRequired = async (submissionId: number): Promise<Result<void>> => await httpService.put<void>(`/${this.path}/${submissionId}/kyc-residence-permit`, {}, { timeout: RESIDENCE_PERMIT_REQUEST_TIMEOUT_MS });

    deactivateUser = async (userId: number): Promise<Result<void>> => await httpService.put<void>(`/${this.path}/${userId}/deactivate`, {});

    resetEmail = async (individualId: number): Promise<Result<void>> => await httpService.put<void>(`/${this.path}/${individualId}/reset-email`, {});

    resetPhone = async (individualId: number): Promise<Result<void>> => await httpService.put<void>(`/${this.path}/${individualId}/reset-phone`, {});

    delete = async (userId: number): Promise<Result<void>> => await httpService.delete<void>(`/${this.path}/${userId}`);

    getAvailableTriggers = async (userId: number): Promise<Result<IndividualStatus[]>> => await httpService.get<IndividualStatus[]>(`/${this.path}/${userId}/triggers`);

    getIndividualDocumentsTypes = async (): Promise<Result<DocumentTypeModel[]>> => await httpService.get<DocumentTypeModel[]>(`/${this.path}/individual-document-types`);

    getSourceOfFunds = async (): Promise<Result<SelectItem[]>> => {
        var sourceOfFunds = await httpService.get<SelectItem[]>(`/${this.path}/source-of-funds`);
        if (sourceOfFunds.data) {
            sourceOfFunds.data.sort((a, b) => (a.label > b.label) ? 1 : -1);
        }
        return sourceOfFunds
    };

    getStatusTypes = async (): Promise<Result<SelectItem<number>[]>> => await httpService.get<SelectItem<number>[]>(`/${this.path}/statusTypes`);

    updatesKycLevel = async (user: User): Promise<Result<User>> => await httpService.put<User>(`/${this.path}/kyclevel`, user);

    blockUser = async (userId: number): Promise<Result<User>> => await httpService.put<User>(`/${this.path}/${userId}/block`, {});

    activateUser = async (userId: number): Promise<Result<User>> => await httpService.put<User>(`/${this.path}/${userId}/activate`, {});

    getMainActivity = async (): Promise<Result<SelectItem[]>> => {
        const mainActivity = await httpService.get<SelectItem[]>(`/${this.path}/main-activity`);
        if (mainActivity.data) {
            mainActivity.data.sort((a, b) => (a.label > b.label) ? 1 : -1);
        }
        return mainActivity;
    };

    getFinancingMethod = async (): Promise<Result<SelectItem[]>> => {
        const financingMethod = await httpService.get<SelectItem[]>(`/${this.path}/financing-method`);
        if (financingMethod.data) {
            financingMethod.data.sort((a, b) => (a.label > b.label) ? 1 : -1);
        }
        return financingMethod;
    };

    getAccountPurpose = async (): Promise<Result<SelectItem[]>> => {
        const accountPurpose = await httpService.get<SelectItem[]>(`/${this.path}/account-purpose`);
        if (accountPurpose.data) {
            accountPurpose.data.sort((a, b) => (a.label > b.label) ? 1 : -1);
        }
        return accountPurpose;
    };

    getActiveIndividual = async (): Promise<Result<IndividualList[]>> => await httpService.get<IndividualList[]>(`/${this.path}/get-active-individuals`);

    validateIban = async (request: ValidateIbanRequest): Promise<Result<ValidateIbanResponse>> =>
        await httpService.post<ValidateIbanResponse>(`/Iban`, request);

    updateUserPhoto = async (model: UpdateUserPhotoRequest): Promise<Result<string>> => await httpService.post<string>(`/${this.path}/update-user-photo`, model);

}

const userApiService = new UsersApiService();

export { userApiService };
